'use client'

import useDateTime from '@hooks/client/useDateTime'
import { BENEFITS_CAPTIONS } from '@lib/constants'

import AiEstimatedLabel from './ai-estimated-label'
import Chip from './chip'

import type { JSX } from 'react'

type JobFactsProps = {
  salary?: string
  isSalaryEstimated?: boolean
  contractType?: string
  location?: string
  postedAt?: number
  benefits?: (keyof typeof BENEFITS_CAPTIONS)[]
}

/** The benefits chip list. */
export function Benefits({ benefits }: { benefits: (keyof typeof BENEFITS_CAPTIONS)[] }): JSX.Element {
  return (
    <ul className="flex flex-wrap gap-2">
      {benefits.map((b) => (
        <li key={b}>
          <Chip>{BENEFITS_CAPTIONS[b]}</Chip>
        </li>
      ))}
    </ul>
  )
}

/** The job detail's key facts: a description list (each row only when given) and a benefits chip list. */
export default function JobFacts({
  salary,
  isSalaryEstimated,
  contractType,
  location,
  postedAt,
  benefits,
}: JobFactsProps): JSX.Element {
  const dateTime = useDateTime()
  const rows: [string, JSX.Element | string | undefined][] = [
    [
      'Salary',
      salary ? (
        <span className="inline-flex flex-wrap items-center gap-2">
          {salary}
          {isSalaryEstimated && <AiEstimatedLabel mode="disclosure" />}
        </span>
      ) : undefined,
    ],
    ['Contract type', contractType],
    ['Location', location],
    ['Posted', postedAt ? dateTime(postedAt).fromNow() : undefined],
  ]

  return (
    <div className="flex flex-col gap-4 font-sans text-base text-ink">
      <dl className="flex flex-col gap-2">
        {rows.map(([term, value]) =>
          value ? (
            <div key={term} className="flex gap-4">
              <dt className="w-32 text-ink-muted">{term}</dt>
              <dd>{value}</dd>
            </div>
          ) : null,
        )}
      </dl>
      {benefits && benefits.length > 0 && <Benefits benefits={benefits} />}
    </div>
  )
}
